import { Droplets, Hammer, ClipboardCheck } from "lucide-react";

const PILLARS = [
    {
        icon: Droplets,
        n: "01",
        title: "Water-first design",
        body: "Utah's Great Salt Lake is at historic lows. Every scope we install starts with a water budget — not an afterthought.",
    },
    {
        icon: Hammer,
        n: "02",
        title: "Trades-grade execution",
        body: "Foremen ran civil and concrete crews before turf. We read plans, hold elevations, and protect adjacent scopes.",
    },
    {
        icon: ClipboardCheck,
        n: "03",
        title: "Closeout that closes",
        body: "Punch lists handled inside one walk. As-builts, O&M manuals, and warranty docs delivered with final pay app.",
    },
];

export const About = () => {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative py-24 md:py-32 bg-ink text-bone overflow-hidden"
        >
            {/* Topographic SVG background */}
            <svg
                className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none"
                viewBox="0 0 1200 800"
                preserveAspectRatio="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                {[...Array(14)].map((_, i) => (
                    <path
                        key={i}
                        d={`M0 ${100 + i * 50} Q 300 ${60 + i * 50}, 600 ${110 + i * 50} T 1200 ${90 + i * 50}`}
                        fill="none"
                        stroke="#C05B43"
                        strokeWidth="1"
                    />
                ))}
            </svg>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5 reveal">
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-5">
                            <span className="inline-block w-8 h-px bg-terracotta align-middle mr-3" />
                            About H2No Utah
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            Landscape <br />
                            <span className="italic font-light text-terracotta">for the next</span> <br />
                            Utah.
                        </h2>
                        <p className="mt-8 text-base md:text-lg text-bone/80 leading-relaxed">
                            We started H2No Utah, LLC to put a serious, schedule-first landscape
                            subcontractor on the Wasatch Front — one built around the reality of
                            Utah's water future.
                        </p>
                        <p className="mt-4 text-base text-bone/70 leading-relaxed">
                            From new townhomes in Utah Valley to townhome streetscapes in Daybreak
                            to custom estates in Weber Valley, our crews deliver waterwise planting,
                            turf, retaining walls, hardscapes, and fencing as a single scope your
                            superintendent can rely on. We install traditional sod when the spec
                            calls for it — but we believe waterwise wins.
                        </p>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 gap-px bg-bone/15 border border-bone/15">
                            {PILLARS.map((p, i) => (
                                <div
                                    key={p.n}
                                    className="reveal bg-ink p-8 md:p-10 group"
                                    style={{ transitionDelay: `${i * 80}ms` }}
                                    data-testid={`about-pillar-${i}`}
                                >
                                    <div className="flex gap-8 items-start">
                                        <div className="font-heading text-5xl md:text-6xl font-light text-terracotta tracking-tight tabular-nums leading-none">
                                            {p.n}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <p.icon size={20} className="text-bone/60" strokeWidth={1.5} />
                                                <h3 className="font-heading text-2xl md:text-3xl font-medium text-bone tracking-tight">
                                                    {p.title}
                                                </h3>
                                            </div>
                                            <p className="text-sm md:text-base text-bone/70 leading-relaxed max-w-xl">
                                                {p.body}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Service area */}
                        <div className="mt-8 border border-bone/15 p-6 md:p-8 bg-ink reveal">
                            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-bone/50 mb-4">
                                Service Area
                            </div>
                            <div className="flex flex-wrap gap-x-6 gap-y-3 font-heading text-lg md:text-xl">
                                {["Weber County", "Davis County", "Summit County", "Salt Lake Valley", "Utah Valley"].map((c) => (
                                    <span key={c} className="text-bone/85 hover:text-terracotta transition-colors cursor-default">
                                        {c}
                                        <span className="text-bone/30 ml-6 last:hidden">·</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
